import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, FilePlus2, Handshake, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, projectStatusLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate, formatMoney, localIsoDate } from '../lib/format';
import { DownloadButton } from '../components/DownloadButton';
import { RichText } from '../components/RichTextLazy';
import { useClientOptions } from './Invoices';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Select,
  Table,
  Td,
  Th,
  Tr,
  statusTone,
  type BadgeTone,
} from '../components/ui';

/**
 * Contracts (operator's request, 2026-10-06) — support contracts with clients,
 * under Payments. A contract's term is cut into billing periods — calendar
 * months, contract years, or the whole — and **each period is invoiced by hand**
 * (operator, 2026-10-27): Create invoice opens the ordinary invoice form filled
 * in for it, and nothing is saved until it is submitted. The rules live in the
 * API (domain/contract-schedule.ts); these pages show what it decided.
 */

export interface ContractRow {
  id: string;
  number: string;
  title: string;
  client: { id: string; code: string; companyName: string; companyNameAr: string | null };
  /** With its status: a contract keeps a finished project in support while it runs. */
  project: { id: string; code: string; name: string; nameAr: string | null; status?: string } | null;
  startDate: string;
  endDate: string;
  value: number;
  valueBasis: 'MONTH' | 'YEAR' | 'TOTAL';
  billing: 'MONTHLY' | 'YEARLY' | 'ONE_TIME';
  currency: string;
  status: 'ACTIVE' | 'COMPLETED' | 'TERMINATED';
  invoiceCount: number;
  /** Begun periods with no invoice yet: they wait for someone to invoice them. */
  dueCount: number;
  /** The next period to invoice — the first begun one, else the first to come. */
  nextPeriod: { start: string; end: string; amount: number; due: boolean } | null;
}

/** A running contract that has not reached its start date reads as "Upcoming". */
export function contractStatus(contract: { status: string; startDate: string }, today = localIsoDate()): string {
  return contract.status === 'ACTIVE' && contract.startDate > today ? 'UPCOMING' : contract.status;
}

function contractTone(status: string): BadgeTone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'UPCOMING':
      return 'info';
    case 'TERMINATED':
      return 'error';
    default:
      return 'neutral';
  }
}

export function ContractStatusBadge({ contract }: { contract: { status: string; startDate: string } }) {
  const { t } = useI18n();
  const status = contractStatus(contract);
  return <Badge tone={contractTone(status)}>{t(`contracts.status.${status}` as TranslationKey)}</Badge>;
}

/** "US$1,000.00 a month" — the value with what it is for. */
export function useContractValue() {
  const { t, locale } = useI18n();
  return (contract: { value: number; valueBasis: string; currency: string }) =>
    t(`contracts.valuePer.${contract.valueBasis}` as TranslationKey, {
      amount: formatMoney(contract.value, contract.currency, locale),
    });
}

function useClientName() {
  const { locale } = useI18n();
  return (client: { companyName: string; companyNameAr: string | null }) =>
    locale === 'ar' && client.companyNameAr ? client.companyNameAr : client.companyName;
}

/** The contracts table: the list page's, and a client's or a project's. */
export function ContractsTable({ rows, showClient = true }: { rows: ContractRow[]; showClient?: boolean }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const valueOf = useContractValue();
  const clientName = useClientName();

  return (
    <Table>
      <thead>
        <tr>
          <Th className="whitespace-nowrap">{t('contracts.number')}</Th>
          <Th>{showClient ? t('contracts.titleAndClient') : t('contracts.contractTitle')}</Th>
          <Th>{t('contracts.runs')}</Th>
          <Th>{t('common.status')}</Th>
          <Th align="end">{t('contracts.value')}</Th>
          <Th align="end" className="whitespace-nowrap">{t('contracts.toInvoice')}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((contract) => {
          const under = [showClient ? clientName(contract.client) : null, contract.project?.code].filter(Boolean);
          return (
            <Tr key={contract.id} onClick={() => navigate(`/contracts/${contract.id}`)}>
              <Td className="whitespace-nowrap">
                <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{contract.number}</span>
              </Td>
              {/* Takes whatever width the other columns leave, and truncates within it. */}
              <Td className="w-full min-w-[10rem] max-w-0">
                <span className="block truncate" title={contract.title}>{contract.title}</span>
                {under.length > 0 && (
                  <span className="block truncate text-caption text-muted">{under.join(' · ')}</span>
                )}
              </Td>
              <Td className="whitespace-nowrap tabular text-muted">
                {formatDate(contract.startDate, locale)} – {formatDate(contract.endDate, locale)}
              </Td>
              <Td>
                <ContractStatusBadge contract={contract} />
              </Td>
              <Td align="end" className="whitespace-nowrap tabular">
                {formatMoney(contract.value, contract.currency, locale)}
                <span className="block text-caption text-muted">
                  {t(`contracts.per.${contract.valueBasis}` as TranslationKey)} ·{' '}
                  {t(`contracts.billing.${contract.billing}` as TranslationKey)}
                </span>
              </Td>
              <Td align="end" className="whitespace-nowrap tabular text-muted">
                {contract.dueCount > 0 ? (
                  // Begun and waiting: nothing invoices them but a person.
                  <Badge tone="warning">{t('contracts.dueCount', { count: contract.dueCount })}</Badge>
                ) : contract.nextPeriod ? (
                  <>
                    {formatDate(contract.nextPeriod.start, locale)}
                    <span className="block text-caption">
                      {formatMoney(contract.nextPeriod.amount, contract.currency, locale)}
                    </span>
                  </>
                ) : (
                  '—'
                )}
              </Td>
            </Tr>
          );
        })}
      </tbody>
    </Table>
  );
}

export function ContractsPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const clients = useClientOptions();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['contracts', page, filters],
    queryFn: () => api.get<Paginated<ContractRow>>('/contracts', { page, pageSize: 25, ...filters }),
  });

  const filter = (key: string, value: string) => {
    setFilters({ ...filters, [key]: value });
    setPage(1);
  };

  return (
    <>
      <PageHeader
        title={t('contracts.title')}
        subtitle={t('contracts.subtitle')}
        count={data?.total}
        action={
          can(P.CONTRACTS_MANAGE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/contracts/new')}>
              {t('contracts.new')}
            </Button>
          )
        }
      />

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            placeholder={t('common.search')}
            aria-label={t('common.search')}
            value={filters.q ?? ''}
            onChange={(e) => filter('q', e.target.value)}
          />
          <Select value={filters.clientId ?? ''} onChange={(e) => filter('clientId', e.target.value)}>
            <option value="">{t('invoices.client')} — {t('common.all')}</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
              </option>
            ))}
          </Select>
          <Select value={filters.billing ?? ''} onChange={(e) => filter('billing', e.target.value)}>
            <option value="">{t('contracts.billing')} — {t('common.all')}</option>
            {(['MONTHLY', 'YEARLY', 'ONE_TIME'] as const).map((b) => (
              <option key={b} value={b}>{t(`contracts.billing.${b}`)}</option>
            ))}
          </Select>
          <Select value={filters.status ?? ''} onChange={(e) => filter('status', e.target.value)}>
            <option value="">{t('common.status')} — {t('common.all')}</option>
            {(['ACTIVE', 'COMPLETED', 'TERMINATED'] as const).map((s) => (
              <option key={s} value={s}>{t(`contracts.status.${s}`)}</option>
            ))}
          </Select>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<Handshake className="h-8 w-8" />} />
        ) : (
          <>
            <ContractsTable rows={data.items} />
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

/** A client's contracts — the Contracts tab on the client's page. */
export function ClientContractsTab({ clientId }: { clientId: string }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['contracts', 'client', clientId],
    queryFn: () => api.get<Paginated<ContractRow>>('/contracts', { clientId, pageSize: 200 }),
  });

  return (
    <Card padded={false}>
      <div className="flex items-center justify-between gap-3 p-4">
        <p className="text-body-sm text-muted">{t('contracts.subtitle')}</p>
        {can(P.CONTRACTS_MANAGE) && (
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => navigate(`/contracts/new?clientId=${clientId}`)}>
            {t('contracts.new')}
          </Button>
        )}
      </div>
      {isLoading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data?.items.length ? (
        <EmptyState icon={<Handshake className="h-8 w-8" />} />
      ) : (
        <ContractsTable rows={data.items} showClient={false} />
      )}
    </Card>
  );
}

/**
 * A project's contracts, on its money page — and, for whoever keeps contracts,
 * New support contract, filled in with the project and its client: a finished
 * project takes tasks and tickets only while a contract covers the day. Nothing
 * is drawn for a project of no client, or for a reader when it has none.
 */
export function ProjectContractsCard({ projectId, clientId }: { projectId: string; clientId: string | null }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['contracts', 'project', projectId],
    queryFn: () => api.get<Paginated<ContractRow>>('/contracts', { projectId, pageSize: 200 }),
    enabled: can(P.CONTRACTS_VIEW),
  });
  const mayCreate = can(P.CONTRACTS_MANAGE) && !!clientId;
  if (!data || (!data.items.length && !mayCreate)) return null;

  return (
    <Card padded={false}>
      <CardHeader
        title={t('nav.contracts')}
        className="p-5 pb-0"
        action={
          mayCreate && (
            <Button
              icon={<Plus className="h-4 w-4" />}
              onClick={() => navigate(`/contracts/new?clientId=${clientId}&projectId=${projectId}`)}
            >
              {t('contracts.newSupport')}
            </Button>
          )
        }
      />
      {data.items.length ? (
        <ContractsTable rows={data.items} showClient={false} />
      ) : (
        <p className="px-5 py-4 text-body-sm text-muted">{t('contracts.noneForProject')}</p>
      )}
    </Card>
  );
}

/**
 * Who signs for one side, as the PDF prints it: a name and position, or a line
 * left for the pen. Each label sits above its value, not beside it: in the
 * narrow column "Position of person in charge" would leave the value no room.
 */
function Signatory({ side, name, position }: { side: string; name: string | null; position: string | null }) {
  const { t } = useI18n();
  const row = (label: string, text: string | null) => (
    <div className="py-1.5">
      <dt className="text-caption text-[var(--text-tertiary)]">{label}</dt>
      <dd className="mt-0.5 break-words text-body-sm">
        {text ? (
          <span dir="auto" className="block text-start text-[var(--text-primary)]">{text}</span>
        ) : (
          <span className="text-muted">{t('contracts.toFillIn')}</span>
        )}
      </dd>
    </div>
  );
  return (
    <div>
      <p className="mb-1 text-overline uppercase text-[var(--text-tertiary)]">{side}</p>
      <dl className="divide-y divide-[var(--line)]">
        {row(t('contracts.personInCharge'), name)}
        {row(t('contracts.positionOfPerson'), position)}
      </dl>
    </div>
  );
}

const PERIOD_TONE: Record<string, BadgeTone> = {
  DUE: 'warning',
  UPCOMING: 'info',
  SKIPPED: 'neutral',
  NOT_BILLED: 'neutral',
};

/** One billing period on the contract page, with its invoice and any cancelled before it. */
interface PeriodRow {
  start: string;
  end: string;
  amount: number;
  state: 'INVOICED' | 'DUE' | 'UPCOMING' | 'SKIPPED' | 'NOT_BILLED';
  invoice: { id: string; number: string; status: string; totalAmount: number; paidAmount: number } | null;
  cancelled: Array<{ id: string; number: string }>;
}

export function ContractDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const valueOf = useContractValue();
  const clientName = useClientName();

  const { data: contract, isLoading, error, refetch } = useQuery({
    queryKey: ['contract', id],
    queryFn: () => api.get<any>(`/contracts/${id}`),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const money = (n: number) => formatMoney(n, contract.currency, locale);
  const skippedAny = contract.schedule.some((p: any) => p.state === 'SKIPPED');

  return (
    <>
      <div className="mb-4">
        <Link to="/contracts" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('contracts.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{contract.number}</span>
            {/* Typed by a person, in either language: it reads its own way. */}
            <span dir="auto">{contract.title}</span>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <ContractStatusBadge contract={contract} />
            <Badge tone="neutral">{t(`contracts.billing.${contract.billing}` as TranslationKey)}</Badge>
            <Badge tone="info">{contract.currency}</Badge>
            <Link to={`/clients/${contract.client.id}`}>
              <Badge tone="neutral">{contract.client.code} — {clientName(contract.client)}</Badge>
            </Link>
            {contract.project && (
              <Link to={`/projects/${contract.project.id}`}>
                <Badge tone="neutral">{contract.project.code} — {localized(contract.project)}</Badge>
              </Link>
            )}
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            {/* The API draws it in the language the contract is written in. */}
            <DownloadButton path={`/contracts/${id}/pdf`} locale={null} title={t('contracts.downloadPdfHint')}>
              {t('contracts.downloadPdf')}
            </DownloadButton>
            {contract.can.edit && <Button onClick={() => navigate(`/contracts/${id}/edit`)}>{t('common.edit')}</Button>}
            {contract.can.terminate && (
              <Button variant="danger" onClick={() => navigate(`/contracts/${id}/terminate`)}>
                {t('contracts.terminate')}
              </Button>
            )}
          </div>
        }
      />

      {contract.clientInactive && contract.status === 'ACTIVE' && (
        <Card className="mb-4 border-warning/40">
          <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('contracts.clientInactive')}</p>
        </Card>
      )}
      {contract.status === 'TERMINATED' && (
        <Card className="mb-4 border-error/40">
          <p className="text-body-sm font-medium text-error-fg dark:text-error-dark">
            {t('contracts.terminatedOn', {
              date: formatDate(contract.terminatedAt, locale),
              name: localized(contract.terminatedBy),
            })}
          </p>
          <p dir="auto" className="mt-1 whitespace-pre-wrap text-start text-body-sm text-muted">
            {contract.terminationReason}
          </p>
        </Card>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="grid min-w-0 grid-cols-1 content-start gap-5 lg:col-span-2">
          <Card padded={false}>
            <CardHeader title={t('contracts.schedule')} subtitle={t('contracts.byHandHint')} className="p-5 pb-0" />
            <Table>
              <thead>
                <tr>
                  <Th>{t('contracts.period')}</Th>
                  <Th align="end">{t('common.amount')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end" />
                </tr>
              </thead>
              <tbody>
                {contract.schedule.map((period: PeriodRow) => {
                  const open = period.state === 'DUE' || period.state === 'UPCOMING';
                  return (
                    <Tr key={period.start} className={period.state === 'SKIPPED' || period.state === 'NOT_BILLED' ? 'opacity-60' : undefined}>
                      <Td className="whitespace-nowrap tabular">
                        {formatDate(period.start, locale)} – {formatDate(period.end, locale)}
                      </Td>
                      <Td align="end" className="whitespace-nowrap tabular">{money(period.amount)}</Td>
                      <Td>
                        {period.invoice ? (
                          <span className="flex flex-wrap items-center gap-2">
                            <Link
                              to={`/invoices/${period.invoice.id}`}
                              className="font-mono text-mono-sm text-primary-600 dark:text-primary-400 hover:underline"
                            >
                              {period.invoice.number}
                            </Link>
                            <Badge tone={statusTone(period.invoice.status)}>{enumLabel(period.invoice.status, locale)}</Badge>
                            {period.invoice.paidAmount > 0 && period.invoice.paidAmount < period.invoice.totalAmount && (
                              <span className="text-caption text-muted">
                                {t('contracts.paidOf', {
                                  paid: money(period.invoice.paidAmount),
                                  total: money(period.invoice.totalAmount),
                                })}
                              </span>
                            )}
                          </span>
                        ) : (
                          <Badge tone={PERIOD_TONE[period.state] ?? 'neutral'}>
                            {t(`contracts.state.${period.state}` as TranslationKey)}
                          </Badge>
                        )}
                        {/* A cancelled invoice frees its period: kept here, so the history stays with it. */}
                        {period.cancelled.length > 0 && (
                          <span className="mt-1 block text-caption text-muted">
                            {t('contracts.cancelledOn', { numbers: '' })}
                            {period.cancelled.map((inv, i) => (
                              <span key={inv.id}>
                                {i > 0 && ', '}
                                <Link to={`/invoices/${inv.id}`} className="font-mono hover:underline">
                                  {inv.number}
                                </Link>
                              </span>
                            ))}
                          </span>
                        )}
                      </Td>
                      <Td align="end" className="whitespace-nowrap">
                        {open && contract.can.invoice && (
                          <Button
                            size="sm"
                            variant={period.state === 'DUE' ? 'primary' : 'ghost'}
                            icon={<FilePlus2 className="h-4 w-4" />}
                            onClick={() => navigate(`/invoices/new?contractId=${id}&period=${period.start}`)}
                          >
                            {period.state === 'DUE' ? t('contracts.createInvoice') : t('contracts.createInvoiceAhead')}
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
            {skippedAny && <p className="px-5 py-3 text-caption text-muted">{t('contracts.skippedHint')}</p>}
          </Card>

          {contract.preamble || contract.clauses.length || contract.conclusion ? (
            <>
              {contract.preamble && (
                <Card>
                  <CardHeader title={t('contracts.preamble')} />
                  <RichText html={contract.preamble} />
                </Card>
              )}
              {contract.clauses.length > 0 && (
                <Card>
                  <CardHeader title={t('contracts.clauses')} />
                  <ol className="grid gap-5">
                    {contract.clauses.map((clause: { title: string | null; body: string | null }, index: number) => (
                      <li key={index} className="border-t hairline pt-4 first:border-t-0 first:pt-0">
                        <p className="text-overline uppercase text-primary-600 dark:text-primary-400">{t('contracts.clause', { n: index + 1 })}</p>
                        {clause.title && (
                          // Typed by a person, in either language: it reads its own way.
                          <h4 dir="auto" className="mt-1 text-start text-title text-[var(--text-primary)]">
                            {clause.title}
                          </h4>
                        )}
                        {clause.body && (
                          <div className="mt-2">
                            <RichText html={clause.body} />
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                </Card>
              )}
              {contract.conclusion && (
                <Card>
                  <CardHeader title={t('contracts.conclusion')} />
                  <RichText html={contract.conclusion} />
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardHeader title={t('contracts.wording')} />
              <p className="text-body-sm text-muted">{t('contracts.nothingWritten')}</p>
            </Card>
          )}
        </div>

        <div className="grid content-start gap-5">
          <Card>
            <CardHeader title={t('contracts.money')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('contracts.summary.total')}>
                <strong>{money(contract.summary.total)}</strong>
              </Detail>
              <Detail label={t('contracts.summary.invoiced')}>{money(contract.summary.invoiced)}</Detail>
              <Detail label={t('contracts.summary.collected')}>
                <span className="text-success-fg dark:text-success">{money(contract.summary.collected)}</span>
              </Detail>
              <Detail label={t('contracts.summary.outstanding')}>
                <span className={contract.summary.outstanding > 0 ? 'text-warning-fg dark:text-warning-dark' : undefined}>
                  {money(contract.summary.outstanding)}
                </span>
              </Detail>
              {/* Not owed yet — only still to bill: kept apart from what its invoices still owe. */}
              <Detail label={t('contracts.summary.notInvoiced')}>{money(contract.summary.notInvoiced)}</Detail>
              <Detail label={t('contracts.summary.upcoming')}>{money(contract.summary.upcoming)}</Detail>
              <Detail label={t('contracts.nextPeriod')}>
                {contract.nextPeriod ? (
                  <>
                    {formatDate(contract.nextPeriod.start, locale)}
                    <span className="ms-2 text-muted">{money(contract.nextPeriod.amount)}</span>
                  </>
                ) : (
                  '—'
                )}
              </Detail>
            </dl>
            {contract.summary.otherCurrencyInvoices > 0 && (
              <p className="mt-2 text-caption text-muted">
                {t('contracts.summary.otherCurrency', { count: contract.summary.otherCurrencyInvoices })}
              </p>
            )}
          </Card>

          {/* Paid only through its invoices: each payment names the invoice it was received against. */}
          {contract.payments && (
            <Card padded={false}>
              <CardHeader title={t('contracts.payments')} subtitle={t('contracts.paymentsHint')} className="p-5 pb-0" />
              {contract.payments.length === 0 ? (
                <p className="px-5 py-4 text-body-sm text-muted">{t('contracts.noPayments')}</p>
              ) : (
                <ul className="divide-y divide-[var(--line)] px-5 pb-3">
                  {contract.payments.map(
                    (p: {
                      id: string;
                      number: string;
                      amount: number;
                      currency: string;
                      paymentDate: string;
                      paymentMethod: string;
                      reversed: boolean;
                      invoice: { id: string; number: string };
                    }) => (
                      <li key={p.id} className={p.reversed ? 'py-2.5 opacity-60' : 'py-2.5'}>
                        <div className="flex items-baseline justify-between gap-3">
                          <span className="tabular text-body-sm text-[var(--text-primary)]">
                            {formatMoney(p.amount, p.currency, locale)}
                          </span>
                          <span className="tabular text-caption text-muted">{formatDate(p.paymentDate, locale)}</span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-caption text-muted">
                          <Link to={`/invoices/${p.invoice.id}`} className="font-mono text-primary-600 dark:text-primary-400 hover:underline">
                            {p.invoice.number}
                          </Link>
                          <span>{p.paymentMethod.replace('_', ' ')}</span>
                          {p.reversed && <Badge tone="neutral">{t('contracts.reversed')}</Badge>}
                        </div>
                      </li>
                    ),
                  )}
                </ul>
              )}
            </Card>
          )}

          <Card>
            <CardHeader title={t('contracts.terms')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('contracts.value')}>{valueOf(contract)}</Detail>
              <Detail label={t('contracts.billing')}>{t(`contracts.billing.${contract.billing}` as TranslationKey)}</Detail>
              <Detail label={t('contracts.startDate')}>{formatDate(contract.startDate, locale)}</Detail>
              <Detail label={t('contracts.endDate')}>{formatDate(contract.endDate, locale)}</Detail>
              <Detail label={t('common.currency')}>
                <span className="font-mono text-mono-sm">{contract.currency}</span>
              </Detail>
              <Detail label={t('contracts.paymentTerms')}>
                <span className="tabular">{contract.paymentTermsDays}</span>
              </Detail>
              <Detail label={t('invoices.project')}>
                {contract.project ? `${contract.project.code} — ${localized(contract.project)}` : t('invoices.noProject')}
                {/* A contract keeps a finished project in support while it runs. */}
                {contract.project?.status && (
                  <Badge tone={statusTone(contract.project.status)} className="ms-2">
                    {projectStatusLabel(contract.project.status, locale)}
                  </Badge>
                )}
              </Detail>
              <Detail label={t('contracts.entered')}>{formatDate(contract.billFrom, locale)}</Detail>
              <Detail label={t('contracts.createdBy')}>{localized(contract.createdBy)}</Detail>
            </dl>
          </Card>

          <Card>
            <CardHeader title={t('contracts.signatures')} />
            <div className="grid gap-5">
              <Signatory side={t('contracts.ourSide')} name={contract.ourSignatoryName} position={contract.ourSignatoryPosition} />
              <Signatory
                side={t('contracts.clientSide', { client: clientName(contract.client) })}
                name={contract.clientSignatoryName}
                position={contract.clientSignatoryPosition}
              />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
